import type { HTMLAttributes, ReactNode } from 'react';
import './Card.css';

export interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title?: ReactNode;
  /** Short supporting line under the title. */
  description?: ReactNode;
  /** Action area rendered on the right of the header. */
  actions?: ReactNode;
  /** `editorial` uses the warm cream ground used by storytelling pages. */
  tone?: 'default' | 'brand' | 'editorial';
  /**
   * The heading level for `title`. `h2` by default, because a card is a section
   * of a page whose own name is the `h1`.
   *
   * It used to be `h3` unconditionally, which skipped a level on every screen
   * in the product — a reader moving by headings was told there was a missing
   * level between the page and its first section. Overridable because a card
   * nested inside another section is genuinely an `h3`.
   */
  titleAs?: 'h2' | 'h3' | 'h4';
}

export function Card({
  title,
  description,
  actions,
  tone = 'default',
  titleAs: Heading = 'h2',
  className,
  children,
  ...rest
}: CardProps) {
  const classes = ['sc-card', `sc-card--${tone}`, className ?? ''].filter(Boolean).join(' ');

  return (
    <div {...rest} className={classes}>
      {(title || actions) && (
        <div className="sc-card__header">
          <div>
            {title && <Heading className="sc-card__title">{title}</Heading>}
            {description && <p className="sc-card__description">{description}</p>}
          </div>
          {actions && <div className="sc-card__actions">{actions}</div>}
        </div>
      )}
      <div className="sc-card__body">{children}</div>
    </div>
  );
}
